import React from "react";
import { Link } from "react-router-dom";

const TermsAndConditions = () => {
  const sections = [
    {
      title: "1. Agreement to These Terms",
      content: (
        <p>
          These Terms &amp; Conditions govern your access to and use of
          HireCura, a healthcare recruitment platform operated by
          HireCura Technologies Private Limited ("HireCura", "we", "us",
          or "our"). By accessing or using the platform, you agree to
          these Terms. If you do not agree, please do not use the service.
        </p>
      ),
    },
    {
      title: "2. About HireCura",
      content: (
        <p>
          HireCura provides online tools intended to connect healthcare
          professionals with hospitals, clinics, laboratories, healthcare
          institutions, and other employers. Depending on the features
          available, users may create profiles, publish vacancies, search
          for jobs, and submit or review applications. We may modify or
          discontinue features as the platform develops, subject to
          applicable law.
        </p>
      ),
    },
    {
      title: "3. Eligibility and Accounts",
      content: (
        <>
          <p>
            You must be legally eligible to use the relevant services.
            You agree to provide accurate, current, and complete
            information when registering and to keep it updated.
          </p>
          <ul>
            <li>Keep your login credentials secure.</li>
            <li>Do not impersonate another person or organization.</li>
            <li>
              Do not share or transfer your account in a way that violates
              these Terms or applicable law.
            </li>
            <li>
              Notify us if you believe your account has been accessed
              without authorization.
            </li>
          </ul>
          <p>
            You are responsible for activity conducted through your
            account, except where applicable law provides otherwise.
          </p>
        </>
      ),
    },
    {
      title: "4. Candidate Responsibilities",
      content: (
        <>
          <p>As a candidate, you agree to:</p>
          <ul>
            <li>
              Provide truthful information about your identity,
              qualifications, registration, experience, and employment
              history.
            </li>
            <li>
              Apply only for roles for which you are appropriately
              qualified and legally eligible.
            </li>
            <li>
              Upload only resumes, documents, photographs, and other
              materials you are authorized to share.
            </li>
            <li>
              Keep professional licences, registrations, and credentials
              accurate where relevant to the role.
            </li>
            <li>
              Communicate respectfully with employers and other users.
            </li>
          </ul>
          <p>
            You are responsible for checking the terms of an employment
            offer and verifying an employer before accepting a position.
          </p>
        </>
      ),
    },
    {
      title: "5. Employer Responsibilities",
      content: (
        <>
          <p>As an employer or recruiter, you agree to:</p>
          <ul>
            <li>
              Provide accurate information about your organization,
              vacancies, responsibilities, qualifications, location,
              and compensation where stated.
            </li>
            <li>
              Have authority to represent the organization and publish
              the relevant vacancy.
            </li>
            <li>
              Comply with applicable employment, recruitment,
              anti-discrimination, privacy, and other relevant laws.
            </li>
            <li>
              Use candidate information only for legitimate recruitment
              and related purposes and protect it from unauthorized access.
            </li>
            <li>
              Not request payment, sensitive information, or documents
              from candidates for unlawful or deceptive purposes.
            </li>
          </ul>
          <p>
            Employers are responsible for their hiring decisions, candidate
            communications, employment offers, workplace conditions,
            credential checks, and compliance obligations.
          </p>
        </>
      ),
    },
    {
      title: "6. Job Listings and Recruitment Outcomes",
      content: (
        <p>
          HireCura provides a platform to facilitate recruitment. Unless
          expressly stated in a separate written agreement, we do not
          employ candidates, act as the employer for listed vacancies,
          guarantee that a listing is available or accurate, or guarantee
          interviews, offers, hiring, salary, or employment. Users should
          independently verify job listings, organizations, credentials,
          and offers before taking action.
        </p>
      ),
    },
    {
      title: "7. Acceptable Use",
      content: (
        <>
          <p>You must not:</p>
          <ul>
            <li>Publish false, misleading, fraudulent, or unlawful content.</li>
            <li>
              Use the platform to discriminate unlawfully, harass,
              threaten, or deceive others.
            </li>
            <li>
              Post spam, malware, phishing content, or unauthorized
              advertisements.
            </li>
            <li>
              Scrape, harvest, sell, or misuse personal information
              obtained through HireCura.
            </li>
            <li>
              Attempt unauthorized access to accounts, systems, or data.
            </li>
            <li>
              Interfere with platform security, availability, or operation.
            </li>
            <li>
              Infringe another person's intellectual property or other
              legal rights.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: "8. User Content and Information",
      content: (
        <p>
          You retain rights you hold in content you submit, subject to
          applicable law and any separate agreement. You grant HireCura
          permission to host, store, display, process, and transmit that
          content as reasonably necessary to operate and provide the
          services you use. You confirm that you have the rights and
          permissions needed to submit the content. Your personal
          information is handled in accordance with our Privacy Policy.
        </p>
      ),
    },
    {
      title: "9. Intellectual Property",
      content: (
        <p>
          The HireCura name, logo, website design, software, and other
          platform materials are owned by or licensed to HireCura
          Technologies Private Limited, unless otherwise indicated.
          You may use the platform for its intended purposes but may not
          copy, modify, distribute, reverse engineer, or commercially
          exploit protected materials except as permitted by law or
          with prior written permission.
        </p>
      ),
    },
    {
      title: "10. Third-Party Services",
      content: (
        <p>
          The platform may link to or integrate with third-party services.
          Those services may be subject to separate terms and policies.
          HireCura does not control third-party services and is not
          responsible for their content or conduct except where applicable
          law provides otherwise.
        </p>
      ),
    },
    {
      title: "11. Suspension and Account Termination",
      content: (
        <p>
          We may investigate suspected misuse and restrict, suspend, or
          terminate access where reasonably necessary to protect users,
          maintain platform security, enforce these Terms, or comply with
          law. Where appropriate and legally permitted, we will provide
          notice or an opportunity to address the issue. You may stop
          using the platform and request account closure through the
          available account features or by contacting us. Some information
          may be retained as described in our Privacy Policy or where
          required by law.
        </p>
      ),
    },
    {
      title: "12. Disclaimers and Limitation of Liability",
      content: (
        <>
          <p>
            To the extent permitted by law, HireCura is provided on an
            "as available" basis. We do not warrant uninterrupted access,
            error-free operation, or that every listing, profile, employer,
            or candidate can be independently verified.
          </p>
          <p>
            To the extent permitted by applicable law, HireCura Technologies
            Private Limited will not be liable for indirect, incidental,
            special, or consequential losses arising from platform use,
            including losses relating to hiring decisions, third-party
            conduct, or inaccurate user-submitted information. Nothing in
            these Terms excludes or limits liability that cannot legally
            be excluded or limited.
          </p>
        </>
      ),
    },
    {
      title: "13. Indemnity",
      content: (
        <p>
          To the extent permitted by applicable law, you may be responsible
          for losses, claims, or reasonable costs arising from your
          unlawful use of the platform, your breach of these Terms, or
          content you submit that infringes another person's rights.
          This section does not apply beyond what the law permits.
        </p>
      ),
    },
    {
      title: "14. Changes to These Terms",
      content: (
        <p>
          We may revise these Terms as our services or legal requirements
          change. We will publish the updated Terms on this page and
          update the "Last updated" date. Where required by law, we will
          provide additional notice or obtain consent. Continued use
          after an update takes effect is subject to applicable law.
        </p>
      ),
    },
    {
      title: "15. Governing Law and Disputes",
      content: (
        <p>
          These Terms are governed by the laws of India. Disputes will
          be subject to the jurisdiction of the competent courts and
          authorities as determined under applicable law. Nothing in
          this section limits any rights or remedies that cannot legally
          be waived.
        </p>
      ),
    },
    {
      title: "16. Contact Us",
      content: (
        <>
          <p>
            For questions about these Terms, contact:
          </p>
          <p>
            <strong>HireCura Technologies Private Limited</strong>
            <br />
            Hyderabad, Telangana, India
            <br />
            Email: support@hirecura.com
          </p>
        </>
      ),
    },
  ];

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
      <article className="mx-auto max-w-4xl overflow-hidden rounded-2xl
                          border border-gray-100 bg-white shadow-sm">
        <header className="border-b border-gray-100 px-6 py-10 sm:px-10">
          <Link to="/" className="text-2xl font-bold tracking-tight">
            <span className="text-[#0F172A]">Hire</span>
            <span className="text-[#0D9488]">Cura</span>
          </Link>

          <p className="mt-8 text-sm font-semibold uppercase tracking-widest
                        text-[#0D9488]">
            Legal
          </p>

          <h1 className="mt-2 text-3xl font-bold text-[#0F172A]
                         sm:text-4xl">
            Terms &amp; Conditions
          </h1>

          <p className="mt-4 text-sm text-gray-500">
            Last updated: 15 October 2026
          </p>

          <p className="mt-5 max-w-3xl leading-7 text-gray-600">
            These Terms explain the rules for using HireCura as a candidate,
            employer, recruiter, or visitor.
          </p>
        </header>

        <div className="space-y-8 px-6 py-8 sm:px-10">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-semibold text-[#0F172A]">
                {section.title}
              </h2>
              <div className="mt-3 space-y-3 leading-7 text-gray-600
                              [&_ul]:list-disc [&_ul]:space-y-2
                              [&_ul]:pl-6">
                {section.content}
              </div>
            </section>
          ))}
        </div>

        <div className="border-t border-gray-200 px-6 py-6 sm:px-10">
          <Link
            to="/privacy-policy"
            className="font-medium text-[#0D9488] hover:underline"
          >
            ← Read Privacy Policy
          </Link>
        </div>
      </article>
    </main>
  );
};

export default TermsAndConditions;